import React, {useEffect, useState, useRef} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import InputText from '../../../../components/FormFields/InputText';
import SelectList from '../../../../components/select/SelectList';
import InputDate from '../../../../components/FormFields/inputDate/InputDate';
import {getLanguage, t} from 'react-switch-lang';
import {yupResolver} from '@hookform/resolvers';
import * as yup from 'yup';
import './RevisionsForm.scss';

import {editRevisions, getAllRevisionScopes} from '../../../../services/revisions';
import {useParams} from 'react-router-dom';
import SelectInfinite from '../../../../components/FormFields/SelectInfinite';
import Toast from '../../../../components/toast/Toast';
import Swal from 'sweetalert2';
import {QUARTER, REVISION_MODE} from '../../const';
import {format} from 'date-fns';
import Textarea from '../../../../components/FormFields/Textarea';

const EditRevisionForm = ({editIsDisabled, setEditIsDisabled, data}) => {
  const lang = getLanguage();
  const revisionTypeRef = useRef(false);
  const previousRevisionType = useRef(null);
  const [revisionScopesOptions, setRevisionScopesOptions] = useState([])
  const [disableRevisionScopes, setDisableRevisionScopes] = useState(true)
  const params = useParams();

  const [schema, setSchema] = useState(
    yup.object().shape({
      name: yup
        .string()
        .required(t('message.fieldRequired'))
        .trim()
        .min(3, `${t('message.min', {min: 3})}`)
        .max(255, `${t('message.max', {max: 255})}`),
      revisionType: yup
        .object()
        .required(t('message.fieldRequired'))
        .nullable(),
      revisionScopes: yup.array().nullable().required(t('message.fieldRequired')),
      dateOfIssue: yup.date().required(t('message.fieldRequired')).nullable(),
      revisionMode: yup
        .object()
        .required(t('message.fieldRequired'))
        .nullable(),
      description: yup
        .string()
        .required(t('message.fieldRequired'))
        .trim()
        .min(3, `${t('message.min', {min: 3})}`)
        .max(10000, `${t('message.max', {max: 10000})}`),
      quarter: yup.object().required(t('message.fieldRequired')).nullable(),
    })
  );

  useEffect(() => {
    if (data)
      methods.reset({
        revisionType: {
          value: data?.data?.revisionType?.id,
          label:
            lang === 'mne'
              ? data?.data?.revisionType?.name
              : data?.data?.revisionType?.nameEn,
          abbreviation: data?.data?.revisionType?.abbreviation,
        },
        revisionScopes: data.data.revisionScopes?.map((r) => ({
          value: r?.id,
          label: lang === 'mne' ? r?.name : r?.nameEn,
        })),
        revisionMode: {
          label:
            lang === 'eng'
              ? data?.data?.revisionMode
              : data?.data?.revisionMode === 'REGULAR'
              ? 'REDOVNA'
              : 'VANREDNA',
          value: 1,
        },
        dateOfIssue: new Date(data?.data?.issuedAt),
        quarter: {label: data?.data?.quarter, value: 1},
        description: data?.data?.description,
        name: data?.data?.name
      });
  }, [data]);

  const methods = useForm({
    resolver: yupResolver(schema),
  });

  const {handleSubmit, watch, setValue, errors} = methods;
  const watchRevisionType = watch('revisionType')

  const submitForm = async (formData) => {
    const formatIssueAtDate =
      format(new Date(formData?.dateOfIssue), 'yyyy-MM-dd') +
      'T' +
      format(new Date(formData?.dateOfIssue), 'hh:mm:ss');

    const payload = {
      referenceNumber: data?.data?.referenceNumber,
      id: params?.id,
      revisionType: {
        id: formData?.revisionType?.value,
        label: formData?.revisionType.label,
        abbreviation: formData?.revisionType?.abbreviation,
      },
      revisionScopes: formData?.revisionScopes?.map((r) => ({
        id: r.value,
      })),
      revisionMode: formData.revisionMode.value,
      issuedAt: formatIssueAtDate,
      quarter: formData.quarter.label,
      description: formData.description,
      name: formData?.name
    };
    try {
      const response = await editRevisions(payload);
      if (response.status === 200) {
        Toast.fire({
          title: t('departments.message.updateSuccess', {
            entity: t('common.revision'),
          }),
          icon: 'success',
        });
        setEditIsDisabled(true);
      }
    } catch (err) {
      if (err.response.data.code === 'revision-invalid-issuing-date') {
        Swal.fire({
          text: `${t('message.invalidIssuingDate')}`,
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }  else if (err.response.data.code === 'revision-name-exist') {
        Swal.fire({
          text: t('revisions.revisionWithGivenNameExists'),
          icon: 'error',
        });
      }  else if (err?.response?.data?.code === 'revision_scope_could_not_be_removed_from_revision') {
        Swal.fire({
          text: t('revisions.revisionScopeCouldNotBeRemovedFromRevision'),
          icon: 'error',
        });
      } else {
        Swal.fire({
          text: t('message.errorServerText'),
          icon: 'error',
        });
      }
    }
  };
  const fetchAllRevisionScopes = async () => {
    try {
      const {data} = await getAllRevisionScopes(watchRevisionType['value']);
      return data;
    } catch (e) {}
  };
  useEffect(() => {
    const currentRevisionTypeValue = watchRevisionType?.value;

    if (!currentRevisionTypeValue) {
      setDisableRevisionScopes(true);
      setRevisionScopesOptions([]);
      setValue('revisionScopes', [], {
        shouldValidate: false,
        shouldDirty: false,
      });
      previousRevisionType.current = null;
      return;
    }

    setDisableRevisionScopes(false);

    if (
      previousRevisionType.current !== null &&
      previousRevisionType.current !== currentRevisionTypeValue
    ) {
      setValue('revisionScopes', [], {
        shouldValidate: false,
        shouldDirty: true,
      });
    }

    previousRevisionType.current = currentRevisionTypeValue;

    fetchAllRevisionScopes().then((r) => {
      const options =
        r?.map((item) => ({
          value: item.id,
          label: getLanguage() === 'eng' ? item?.nameEn : item?.name,
        })) || [];

      setRevisionScopesOptions(options);
    });
  }, [watchRevisionType]);

  return (
    <div>
      {
        <FormProvider {...methods}>
          <form className={'revisionForm'} onSubmit={handleSubmit(submitForm)}>
            <div>
              <label className="label-revisionName">
                {t('revisions.revisionName')}
              </label>
              <InputText
                type="string"
                name="name"
                className="form-control input-field"
                placeholder={t('revisions.placeholder.revisionName')}
                disabled={editIsDisabled}
                defaultValue={data?.data?.name}
              />
              <label className="label-referentId pt-3">
                {t('revisions.referenceId')}
              </label>
              <InputText
                name="referenceId"
                className="form-control input-field"
                placeholder={t('revisions.placeholder.referenceId')}
                id="referenceId"
                disabled={true}
                defaultValue={data?.data?.referenceNumber}
                value={data?.data?.referenceNumber}
              />
            </div>
            <div>
              <label className="">{t('revisions.revisionType')}</label>
              <SelectInfinite
                className="select"
                pagination={false}
                noSort={true}
                api="revision-types"
                isRevisionTypes={true}
                name="revisionType"
                id="revisionType"
                placeholder={t('revisions.placeholder.revisionType')}
                labelKey={lang === 'mne' ? 'name' : 'nameEn'}
                isDisabled={editIsDisabled}
              />{' '}
              <label className="pt-3">{t('revisions.area')}</label>
              <SelectList
                name="revisionScopes"
                className={errors.revisionScopesValidation && "error-field"}
                placeholder={t('revisions.placeholder.area')}
                options={revisionScopesOptions}
                isDisabled={disableRevisionScopes || editIsDisabled}
                isMulti
              />
            </div>
            <div>
              <label className="">{t('revisions.types')}</label>
              <SelectList
                name="revisionMode"
                className="select"
                placeholder={t('revisions.placeholder.mode')}
                options={[
                  {
                    label:
                      lang === 'mne'
                        ? REVISION_MODE.REGULAR_MNE
                        : REVISION_MODE.REGULAR,
                    value: REVISION_MODE.REGULAR,
                  },
                  {
                    label:
                      lang === 'mne'
                        ? REVISION_MODE.EXTRAORDINARY_MNE
                        : REVISION_MODE.EXTRAORDINARY,
                    value: REVISION_MODE.EXTRAORDINARY,
                  },
                ]}
                isClearable
                isDisabled={editIsDisabled}
              />{' '}
            </div>
            <div>
              <label className="">{t('revisions.dateOfIssue')}</label>
              <InputDate
                name="dateOfIssue"
                className="form-control input-field dateInput"
                disabled={editIsDisabled}
              />
            </div>
            <div>
              <label className="">{t('revisions.quarter')}</label>
              <SelectList
                name="quarter"
                className="select"
                placeholder={t('revisions.placeholder.quarter')}
                options={[
                  {label: QUARTER.I, value: QUARTER.I},
                  {label: QUARTER.II, value: QUARTER.II},
                  {label: QUARTER.III, value: QUARTER.III},
                  {label: QUARTER.IV, value: QUARTER.IV},
                ]}
                isClearable
                isDisabled={editIsDisabled}
              />
            </div>
            <div>
              <label className="label-referentId">
                {t('revisions.description')}
              </label>
              <Textarea
                type="string"
                name="description"
                disabled={editIsDisabled}
                className="form-control input-field"
                placeholder={t('revisions.placeholder.description')}
                id="description"
              />
            </div>
          </form>
          {!editIsDisabled ? (
            <div className="container-fluid mt-4 footer">
              <div className="row">
                <div className="col-12 pt-4 pb-4 d-flex justify-content-end footer">
                  <button
                    className="secondary-btn"
                    onClick={(event) => {
                      event.preventDefault();
                      methods.reset({
                        referenceId: data?.data?.referenceNumber,
                        revisionType: {
                          value: data?.data?.revisionType?.id,
                          label: data?.data?.revisionType.name,
                          abbreviation: data?.data?.revisionType?.abbreviation,
                        },
                        revisionScopes: data?.data?.revisionScopes?.map((r) => ({
                          value: r?.id,
                          label: lang === 'mne' ? r?.name : r?.nameEn,
                        })),
                        revisionMode: {
                          label: data?.data?.revisionMode,
                          value: 1,
                        },
                        dateOfIssue: new Date('2023-08-01'),
                        quarter: {label: data?.data?.quarter, value: 1},
                        name: data?.data?.name
                      });
                      setEditIsDisabled(true);
                    }}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleSubmit(submitForm)}
                    type="submit"
                    className="primary-btn revisionEditFormButton"
                    disabled={!methods.formState.isDirty}
                  >
                    {t('common.save')}
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </FormProvider>
      }
    </div>
  );
};

export default EditRevisionForm;
